"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, CalendarClock } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import TextareaAutosize from "react-textarea-autosize";

import {
  type BookingRequest,
  type BookingRequestInput,
  bookingRequestSchema,
  toBookingErrorKey,
} from "@/app/(admin)/app/bookings/_schemas/booking";
import {
  LineField,
  lineControlClassName,
  lineControlProps,
} from "@/components/shared/form/line-field";
import {
  SubmitButton,
  type SubmitState,
} from "@/components/shared/form/submit-button";
import { cn } from "@/lib/utils";

import { requestCall } from "../_actions/booking.action";

// =============================================================================

type Slot = { start: Date; end: Date };

type BookingFormProps = {
  slot: Slot;
  timeZone: string;
  onBack: () => void;
  /** The request was stored: the calendar refreshes its slots */
  onBooked: () => void;
};

/**
 * Second step of the booking tab: the chosen slot, then who you are and what
 * the call is about. Validated with the same schema as the server action.
 */
export function BookingForm({
  slot,
  timeZone,
  onBack,
  onBooked,
}: BookingFormProps) {
  const t = useTranslations("Connect.booking.form");
  const locale = useLocale();
  const [state, setState] = useState<SubmitState>("idle");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BookingRequestInput, unknown, BookingRequest>({
    resolver: zodResolver(bookingRequestSchema),
    defaultValues: {
      startsAt: slot.start.toISOString(),
      name: "",
      email: "",
      company: "",
      topic: "",
      message: "",
      timeZone,
      website: "",
    },
    mode: "onTouched",
  });

  const errorOf = (field: keyof BookingRequestInput) => {
    const message = errors[field]?.message;
    return message ? t(`errors.${toBookingErrorKey(message)}`) : undefined;
  };

  const when = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(slot.start);
  const until = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(slot.end);

  const onSubmit = handleSubmit(async (values) => {
    if (state !== "idle") return;
    setState("loading");
    try {
      const response = await requestCall(locale, values);
      if (!response.success) {
        toast.error(response.message);
        setState("idle");
        return;
      }
      toast.success(response.message);
      setState("success");
      onBooked();
    } catch {
      toast.error(t("errors.generic"));
      setState("idle");
    }
  });

  const fields = [
    { name: "name", autoComplete: "name", required: true },
    { name: "email", autoComplete: "email", type: "email", required: true },
    { name: "company", autoComplete: "organization", required: false },
    { name: "topic", autoComplete: "off", required: true },
  ] as const;

  return (
    <form onSubmit={onSubmit} noValidate aria-label={t("title")}>
      <button
        type="button"
        onClick={onBack}
        className="group inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4 transition-transform duration-300 group-hover:-translate-x-1" />
        {t("cancel")}
      </button>

      <div className="mt-6 flex items-start gap-4 rounded-xl border border-foreground/10 bg-surface-1 p-5">
        <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-foreground text-background">
          <CalendarClock className="size-5" />
        </span>
        <div>
          <p className="text-sm text-muted-foreground">{t("when")}</p>
          <p className="mt-1 text-lg font-semibold first-letter:uppercase">
            {when} – {until}
          </p>
        </div>
      </div>

      <div className="mt-[clamp(2.5rem,4vw,3.5rem)] grid gap-x-6 gap-y-[clamp(2.5rem,4vw,3.5rem)] md:grid-cols-2">
        {fields.map((field) => {
          const id = `booking-${field.name}`;
          const error = errorOf(field.name);
          return (
            <LineField
              key={field.name}
              htmlFor={id}
              label={t(field.name)}
              required={field.required}
              error={error}
            >
              <input
                {...register(field.name)}
                {...lineControlProps(id, error)}
                type={"type" in field ? field.type : "text"}
                inputMode={field.name === "email" ? "email" : undefined}
                autoComplete={field.autoComplete}
                aria-required={field.required}
                placeholder={t(`${field.name}Placeholder`)}
                className={lineControlClassName}
              />
            </LineField>
          );
        })}

        <div className="md:col-span-2">
          <LineField
            htmlFor="booking-message"
            label={t("message")}
            required
            error={errorOf("message")}
          >
            <TextareaAutosize
              {...register("message")}
              {...lineControlProps("booking-message", errorOf("message"))}
              aria-required
              minRows={3}
              maxRows={10}
              placeholder={t("messagePlaceholder")}
              className={cn(lineControlClassName, "resize-none")}
            />
          </LineField>
        </div>

        {/* Honeypot: invisible to humans */}
        <input
          {...register("website")}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden
          className="pointer-events-none absolute h-0 w-0 opacity-0"
        />
      </div>

      <div className="mt-[clamp(2.5rem,4vw,3.5rem)]">
        <SubmitButton
          state={state}
          label={t("submit")}
          loadingLabel={t("submitting")}
          successLabel={t("sent")}
        />
      </div>
    </form>
  );
}

// =============================================================================
